import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { PlaybackService } from './playback.service';
import { SpotifyService } from './spotify.service';
import { AIService } from './ai.service';
import { BackendStorageService, PlaylistMetadata } from './backend-storage.service';
import { PlaybackState } from '../../shared/models/spotify.models';

interface PendingChain {
  playlistId: string;
  triggerTrackId: string;
}

/**
 * Chains dynamic playlists so the music never stops: when the LAST track of a
 * dynamic playlist starts playing, the playlist is regenerated in place
 * (style-referenced on its current tracks plus the persisted seed prompt and
 * moods). When that final track ends, playback either advances naturally into
 * the refreshed context or is restarted from the top explicitly.
 *
 * SDK-only (v1): the chain fires only when playback runs on this device's web
 * player — cross-device/casting sessions are untouched.
 */
@Injectable({ providedIn: 'root' })
export class DynamicPlaylistChainService {
  private playbackService = inject(PlaybackService);
  private spotifyService = inject(SpotifyService);
  private aiService = inject(AIService);
  private backendStorage = inject(BackendStorageService);
  private snackBar = inject(MatSnackBar);

  private started = false;
  /** playlistId:trackId keys already handled — one regen attempt per last-track episode. */
  private handledTriggers = new Set<string>();
  private regenInFlight = false;
  private restartInFlight = false;
  private pending: PendingChain | null = null;

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.playbackService.playbackState$.subscribe(state => {
      this.onState(state).catch(error => console.error('[chain] state handler error:', error));
    });
  }

  private async onState(state: PlaybackState | null): Promise<void> {
    if (!state || !state.track_window?.current_track) {
      return;
    }

    const playlistId = this.playlistIdFromContext(state);

    // Left the playlist → abandon any pending continuation.
    if (this.pending && playlistId !== this.pending.playlistId) {
      console.log('[chain] context changed, pending continuation discarded');
      this.pending = null;
    }
    if (!playlistId) {
      return;
    }

    if (this.pending) {
      await this.maybeContinue(state, this.pending);
      return;
    }

    // Trigger: the playlist's last track is playing (empty next_tracks holds
    // under shuffle too) and repeat is off, so the context will actually end.
    if (state.paused || state.repeat_mode !== 0 || state.track_window.next_tracks.length > 0) {
      return;
    }

    const trackId = state.track_window.current_track.id;
    const key = `${playlistId}:${trackId}`;
    if (this.regenInFlight || this.handledTriggers.has(key)) {
      return;
    }
    if (this.handledTriggers.size > 200) {
      this.handledTriggers.clear();
    }
    this.handledTriggers.add(key);

    const metadata = await this.getMetadata(playlistId);
    if (!metadata?.isDynamic) {
      return;
    }

    await this.regenerateInPlace(playlistId, trackId, metadata);
  }

  /**
   * A refreshed playlist is ready — hand playback over to it once the old
   * final track is done.
   */
  private async maybeContinue(state: PlaybackState, pending: PendingChain): Promise<void> {
    const current = state.track_window.current_track;

    // Playback moved past the trigger track — Spotify advanced naturally into
    // the refreshed context, nothing to do.
    if (current.id !== pending.triggerTrackId) {
      console.log('[chain] playback advanced into refreshed playlist naturally');
      this.pending = null;
      return;
    }

    // Classic end-of-context signal: paused at position 0 on the final track.
    if (state.paused && state.position === 0) {
      if (this.restartInFlight) {
        return;
      }
      this.restartInFlight = true;
      this.pending = null;
      try {
        console.log('[chain] playlist finished — restarting refreshed playlist');
        await this.playbackService.play(`spotify:playlist:${pending.playlistId}`);
        this.snackBar.open('Continuing with refreshed playlist', undefined, { duration: 4000 });
      } catch (error) {
        console.error('[chain] failed to restart refreshed playlist:', error);
      } finally {
        this.restartInFlight = false;
      }
    }
  }

  /**
   * Regenerate the playlist in place while its final track plays out, using
   * the same context a manual Regenerate would.
   */
  private async regenerateInPlace(
    playlistId: string,
    triggerTrackId: string,
    metadata: PlaylistMetadata
  ): Promise<void> {
    this.regenInFlight = true;
    try {
      console.log(`[chain] last track of dynamic playlist ${playlistId} — regenerating in place`);
      const tracks = await firstValueFrom(this.spotifyService.getPlaylistTracks(playlistId, 50, 0));
      const songs = tracks
        .filter(item => item?.track?.name)
        .map(item => ({
          title: item.track.name,
          artist: item.track.artists?.[0]?.name || 'Unknown Artist'
        }));

      const response = await this.aiService.generatePlaylist({
        type: 'similar_playlist',
        songs: metadata.useSimilarContext === false ? undefined : songs,
        userRequest: metadata.prompt || undefined,
        moods: metadata.moods && metadata.moods.length > 0 ? metadata.moods : undefined,
        createPlaylist: true,
        playlistId,
        excludeCurrentSongs: true
      });
      if (!response.success) {
        throw new Error(response.error || 'generation failed');
      }

      this.pending = { playlistId, triggerTrackId };
      console.log('[chain] refreshed playlist ready — will continue when this track ends');
    } catch (error) {
      console.error('[chain] failed to generate next playlist:', error);
      const reason = error instanceof Error && error.message ? `: ${error.message}` : '';
      this.snackBar.open(`Could not generate the next playlist${reason}`, 'Close', { duration: 8000 });
    } finally {
      this.regenInFlight = false;
    }
  }

  /** Called at most once per last-track episode, so no caching needed. */
  private async getMetadata(playlistId: string): Promise<PlaylistMetadata | null> {
    try {
      return (await firstValueFrom(this.backendStorage.getPlaylistPreference(playlistId))) ?? null;
    } catch {
      return null; // transient failure: skip chaining for this episode
    }
  }

  private playlistIdFromContext(state: PlaybackState): string | null {
    const uri = state.context?.uri;
    if (!uri) {
      return null;
    }
    const parts = uri.split(':'); // spotify:playlist:<id>
    return parts[1] === 'playlist' ? parts[2] || null : null;
  }
}
